import { BreathingPatternDto, DailyMeditationDto, MeditationPhaseDto } from '../../api';
import { breathingPatternDurationSeconds } from './breathingPlan';

/**
 * Pure session model for the guided meditation player (MI-003).
 *
 * The server owns the clock: durations and the breathing pattern are stamped on
 * the reading by the fixed timeline of MI-002. This module only reshapes what the
 * API returned into an ordered list of phases; it never invents a duration.
 *
 * The one refinement is the breathing phase (MI-004): when the server sends a
 * breathing pattern, the phase lasts exactly the number of cycles that pattern
 * asks for, so the phase cannot end in the middle of a breath. Both numbers come
 * from the server template; the pattern is simply the more specific of the two.
 */

export type MeditationPhaseKey = 'preparation' | 'breathing' | 'intention' | 'closing';

export const MEDITATION_PHASE_ORDER: readonly MeditationPhaseKey[] = [
  'preparation',
  'breathing',
  'intention',
  'closing',
];

export interface MeditationSessionPhase {
  key: MeditationPhaseKey;
  title?: string;
  text?: string;
  focus?: string;
  /** Null when the reading carries no timing for this phase: the phase is then self paced. */
  durationSeconds: number | null;
  breathingPattern?: BreathingPatternDto;
}

const toSessionPhase = (
  key: MeditationPhaseKey,
  phase?: MeditationPhaseDto,
): MeditationSessionPhase | null => {
  if (!phase) {
    return null;
  }

  const hasContent = Boolean(phase.title || phase.text || phase.focus);

  if (!hasContent) {
    return null;
  }

  const declaredDuration =
    typeof phase.durationSeconds === 'number' && phase.durationSeconds > 0
      ? Math.round(phase.durationSeconds)
      : null;
  const patternDuration = breathingPatternDurationSeconds(phase.breathingPattern);

  return {
    key,
    title: phase.title ?? undefined,
    text: phase.text ?? undefined,
    focus: phase.focus ?? undefined,
    durationSeconds: patternDuration ?? declaredDuration,
    breathingPattern: phase.breathingPattern ?? undefined,
  };
};

/** Ordered, playable phases of a daily meditation. Empty when there is nothing to play. */
export const buildMeditationPhases = (
  meditation?: DailyMeditationDto | null,
): MeditationSessionPhase[] => {
  if (!meditation) {
    return [];
  }

  return MEDITATION_PHASE_ORDER.map((key) => toSessionPhase(key, meditation[key])).filter(
    (phase): phase is MeditationSessionPhase => phase !== null,
  );
};

/** Total session length, or null when at least one phase is self paced. */
export const totalMeditationDurationSeconds = (
  phases: readonly MeditationSessionPhase[],
): number | null => {
  if (phases.length === 0 || phases.some((phase) => phase.durationSeconds === null)) {
    return null;
  }

  return phases.reduce((total, phase) => total + (phase.durationSeconds ?? 0), 0);
};

/**
 * Progress of the whole session, between 0 and 1. Uses elapsed time when every
 * phase is timed and falls back to counting phases when some phase is self paced.
 */
export const meditationSessionProgress = (
  phases: readonly MeditationSessionPhase[],
  phaseIndex: number,
  phaseProgress: number,
): number => {
  if (phases.length === 0) {
    return 0;
  }

  const boundedIndex = Math.min(Math.max(phaseIndex, 0), phases.length - 1);
  const boundedPhaseProgress = Math.min(Math.max(phaseProgress, 0), 1);
  const total = totalMeditationDurationSeconds(phases);

  if (total === null || total === 0) {
    return (boundedIndex + boundedPhaseProgress) / phases.length;
  }

  const elapsedBefore = phases
    .slice(0, boundedIndex)
    .reduce((sum, phase) => sum + (phase.durationSeconds ?? 0), 0);
  const currentDuration = phases[boundedIndex].durationSeconds ?? 0;

  return Math.min(1, (elapsedBefore + currentDuration * boundedPhaseProgress) / total);
};

/** Formats a number of seconds as m:ss. */
export const formatSessionTime = (totalSeconds: number): string => {
  const safeSeconds = Math.max(0, Math.floor(totalSeconds));
  const minutes = Math.floor(safeSeconds / 60);
  const seconds = safeSeconds % 60;

  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};
